import { useState } from "react"

interface ServiceItem {
  name: string
  desc: string
  popular?: boolean
  features: string[]
}

interface ServiceTab {
  label: string
  items: ServiceItem[]
}

const tabs: ServiceTab[] = [
  {
    label: "Interior Designing",
    items: [
      {
        name: "Customized Carpentry",
        desc: "Get carpentry crafted just for you.",
        popular: true,
        features: ["Expert carpenters at work", "Custom designs for every room", "High quality wood used"],
      },
      {
        name: "Wardrobe Solutions",
        desc: "Smart storage with style and grace.",
        popular: true,
        features: ["Made to suit your needs", "Durable and stylish wardrobes", "Efficient storage design"],
      },
      {
        name: "Modular Kitchen Design",
        desc: "Functional kitchens that inspire cooking.",
        features: ["Customized modular units", "Optimized kitchen space", "Stylish and practical design"],
      },
      {
        name: "False Ceiling Installation",
        desc: "Upgrade your ceilings with style.",
        features: ["Elegant ceiling designs", "Hides cables and ducts", "Improves room lighting"],
      },
    ],
  },
  {
    label: "Home Renovation",
    items: [
      {
        name: "Painting",
        desc: "Refresh your walls with colors.",
        popular: true,
        features: ["Expert painting team", "Wide color options", "Long lasting results"],
      },
      {
        name: "Wooden Flooring",
        desc: "Warm and elegant flooring options.",
        popular: true,
        features: ["Premium wood types used", "Expert floor fitting", "Elegant polished look"],
      },
      {
        name: "Blinds & Curtains",
        desc: "Stylish window treatments for you.",
        features: ["Wide fabric selection", "Custom tailored sizes", "Enhances room decor"],
      },
      {
        name: "Wallpaper Application",
        desc: "Decorate walls with unique styles.",
        features: ["Wide range of wallpaper", "Expert smooth application", "Adds unique room style"],
      },
    ],
  },
  {
    label: "Structural Works",
    items: [
      {
        name: "UPVC & Aluminum Works",
        desc: "Durable doors and windows made.",
        popular: true,
        features: ["High durability materials", "Custom designs available", "Weatherproof fittings"],
      },
      {
        name: "Glass & Mesh Doors/Windows",
        desc: "Elegant and secure door solutions.",
        popular: true,
        features: ["Safe and sturdy glass", "Mesh to keep insects out", "Stylish door/window look"],
      },
      {
        name: "Lighting Solutions",
        desc: "Brighten spaces with style.",
        features: ["Custom lighting plans", "Modern and energy smart", "Sets perfect room mood"],
      },
      {
        name: "Plumbing and Civil Works",
        desc: "Reliable plumbing and civil work.",
        features: ["Expert plumbing service", "Quality civil construction", "On time project delivery"],
      },
    ],
  },
]

const serviceImages = [
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=500&q=80",
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=500&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&q=80",
  "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=500&q=80",
]

export function Services() {
  const [activeTab, setActiveTab] = useState(0)

  return (
    <section id="services" className="border-t border-gray-200 py-20">
      <div className="mx-auto max-w-[1400px] px-8">
        {/* Header */}
        <div className="reveal text-center">
          <p className="text-[11px] font-light tracking-[0.3em] text-gray-400 uppercase">
            What We Offer
          </p>
          <h2 className="font-heading mt-3 text-[40px] leading-[1.15] text-navy sm:text-[48px]">
            EXPLORE OUR
            <br />
            EXPERT SERVICES
          </h2>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex justify-center gap-0 border-b border-gray-200">
          {tabs.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(i)}
              className={`px-8 pb-3 text-[13px] font-light tracking-[0.1em] uppercase transition-all ${
                activeTab === i
                  ? "border-b-2 border-navy text-navy"
                  : "border-b-2 border-transparent text-gray-400 hover:text-navy"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Image carousel */}
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {serviceImages.map((img, i) => (
            <div key={i} className="group overflow-hidden bg-gray-100">
              <img
                src={img}
                alt={`Service ${i + 1}`}
                className="h-[280px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          ))}
        </div>

        {/* Service cards */}
        <div key={activeTab} className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tabs[activeTab].items.map((item) => (
            <div
              key={item.name}
              className="group border border-gray-200 p-6 transition-all duration-300 hover:border-navy"
            >
              {item.popular && (
                <span className="mb-3 inline-block border border-navy px-3 py-0.5 text-[10px] font-medium tracking-[0.15em] text-navy uppercase">
                  Popular
                </span>
              )}
              <h4 className="font-heading text-lg text-navy">{item.name}</h4>
              <p className="mt-1 text-[13px] font-light text-gray-400">{item.desc}</p>
              <ul className="mt-4 space-y-2">
                {item.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] font-light text-gray-500">
                    <span className="mt-1.5 h-1 w-1 shrink-0 bg-navy" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
